// Cargo / container model for the ocean shipment form.
//
// A cargo lists its containers as type groups with a count (e.g. 2 x 40HC + 1 x 20DC).
// One detail row is generated per container; unit (package count), packing type, gross
// weight, CBM and description are entered on each row. The cargo-level unitQuantity,
// grossWeight, cbm, containerPcs and containerType are kept as totals of the rows, because BL documents, OMBL totals and
// invoices still read them from the cargo.

let groupSeq = 0;
const newGroupId = () => `group-${Date.now()}-${(groupSeq += 1)}`;

export const emptyContainerRow = (groupId, containerType = "", packingType = "") => ({
    groupId,
    containerType,
    containerNo: "",
    sealNumber: "",
    vgm: "",
    vgmPdf: null,
    packingType,
    unitQuantity: "",
    grossWeight: "",
    cbm: "",
    description: "",
});

const sumRows = (containers, key) =>
    containers.reduce((acc, row) => acc + (Number(row[key]) || 0), 0);

export const withContainerTotals = (cargo) => {
    const containers = cargo.containers || [];
    const groups = cargo.containerGroups || [];

    return {
        ...cargo,
        containerPcs: containers.length,
        containerType: groups[0]?.containerType || containers[0]?.containerType || "",
        unitQuantity: sumRows(containers, "unitQuantity"),
        grossWeight: sumRows(containers, "grossWeight"),
        cbm: sumRows(containers, "cbm"),
    };
};

// Gives every group exactly `containerPcs` rows of its type, keeping what was typed in
// existing rows. A row keeps its own type while the group has none (carrier auto-fill).
export const syncContainerRows = (cargo, groups) => {
    const containers = groups.flatMap((group) => {
        const pcs = Math.max(0, Number(group.containerPcs) || 0);
        const existing = (cargo.containers || []).filter((row) => row.groupId === group.id);

        return Array.from({ length: pcs }, (_, i) =>
            existing[i]
                ? { ...existing[i], containerType: group.containerType || existing[i].containerType }
                : emptyContainerRow(group.id, group.containerType, cargo.selectedPackingType || "")
        );
    });

    return withContainerTotals({ ...cargo, containerGroups: groups, containers });
};

export const newContainerGroup = () => ({ id: newGroupId(), containerType: "", containerPcs: 1 });

export const newCargo = () => {
    const group = newContainerGroup();

    return withContainerTotals({
        commodity: "",
        hsCode: "",
        selectedPackingType: "",
        unitQuantity: 0,
        notStackable: true,
        stackingFactor: 2,
        description: "",
        operationType: "ocean",
        goodsOriginCountry: "",
        containerGroups: [group],
        containers: [emptyContainerRow(group.id)],
    });
};

// Saved shipments only store rows; consecutive rows of the same type form one group.
// Older shipments kept gross weight / CBM on the cargo, so those move to the first row.
export const cargoFromSaved = (saved = {}) => {
    const savedRows = Array.isArray(saved.containers) && saved.containers.length
        ? saved.containers
        : Array.from({ length: Math.max(1, Number(saved.containerPcs) || 0) }, () => ({
            containerType: saved.containerType || "",
            containerNo: saved.containerNo || "",
            sealNumber: saved.sealNumber || "",
            vgm: saved.vgm || "",
            vgmPdf: saved.vgmPdf || null,
        }));

    const legacyTotals = !savedRows.some((row) => Number(row.grossWeight) > 0 || Number(row.cbm) > 0);
    // Units were kept on the cargo only; they move to the first row like the totals above.
    const legacyUnits = !savedRows.some((row) => Number(row.unitQuantity) > 0);

    const groups = [];
    const containers = savedRows.map((row, index) => {
        const type = row.containerType || "";
        const last = groups[groups.length - 1];
        if (!last || last.containerType !== type) {
            groups.push({ id: newGroupId(), containerType: type, containerPcs: 0 });
        }
        const group = groups[groups.length - 1];
        group.containerPcs += 1;

        return {
            ...emptyContainerRow(group.id, type, row.packingType || saved.selectedPackingType || ""),
            containerNo: row.containerNo || "",
            sealNumber: row.sealNumber || "",
            vgm: row.vgm || "",
            vgmPdf: row.vgmPdf || null,
            unitQuantity: legacyUnits && index === 0 ? saved.unitQuantity || "" : row.unitQuantity ?? "",
            grossWeight: legacyTotals && index === 0 ? saved.grossWeight || "" : row.grossWeight ?? "",
            cbm: legacyTotals && index === 0 ? saved.cbm || "" : row.cbm ?? "",
            description: row.description || "",
        };
    });

    return withContainerTotals({
        commodity: saved.commodity || "",
        hsCode: saved.hsCode || "",
        selectedPackingType: saved.selectedPackingType || "",
        unitQuantity: saved.unitQuantity || 0,
        notStackable: saved.notStackable ?? true,
        stackingFactor: saved.stackingFactor ?? 2,
        description: saved.description || "",
        operationType: saved.operationType || "ocean",
        goodsOriginCountry: saved.goodsOriginCountry || "",
        containerGroups: groups,
        containers,
    });
};

// Shape sent to the API: totals refreshed, UI-only grouping removed.
export const cargoForPayload = (cargo) => {
    const { containerGroups, ...rest } = withContainerTotals(cargo);

    return {
        ...rest,
        containers: rest.containers.map(({ groupId, ...row }) => ({
            ...row,
            unitQuantity: Number(row.unitQuantity) || 0,
            grossWeight: Number(row.grossWeight) || 0,
            cbm: Number(row.cbm) || 0,
            vgmPdf: typeof row.vgmPdf === "string" ? row.vgmPdf : row.vgmPdf?.name || "",
        })),
    };
};
